import { useEffect, useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import { Button, Dialog, DialogContent, DialogDescription, DialogTitle, Input, Select } from '@/components/ui';
import { useActiveCouriers } from '@/features/shipping/couriers/hooks/useShippingCouriers';
import type { AdminOrderActionRequest } from '@/lib/api-admin/types';
import type { OrderActionType } from '../hooks/useOrderActionMutation';



/**
 * 모달이 렌더 가능한 액션. 5개 타입드-패스 액션 + confirm-purchase(구매확정).
 * confirm-purchase 는 OpenAPI 미반영이라 OrderActionType(타입드 경로) 밖이며,
 * 사유 입력만 필요해 추가 분기 없이 동일 폼을 재사용한다.
 */
export type OrderActionModalAction = OrderActionType | 'confirm-purchase';

export interface OrderActionModalProps {
  action: OrderActionModalAction;
  orderNo: string;
  isOpen: boolean;
  isSubmitting: boolean;
  errorMessage?: string | null;
  expectedPickupCode?: string | null;
  onSubmit: (body: AdminOrderActionRequest) => void;
  onCancel: () => void;
}

const requiresTracking = (action: OrderActionModalAction): boolean =>
  action === 'fulfillment-ready';
const requiresPickupCodeConfirm = (action: OrderActionModalAction): boolean =>
  action === 'pickup-confirm';

export const OrderActionModal = ({
  action,
  orderNo,
  isOpen,
  isSubmitting,
  errorMessage,
  expectedPickupCode,
  onSubmit,
  onCancel,
}: OrderActionModalProps) => {
  const { t } = useTranslation('orders');
  const [reason, setReason] = useState('');
  // QA-510 택배사는 배송 업체 관리의 "사용" 업체. 기본값은 맨 위 업체(고도몰과 같음).
  const { couriers } = useActiveCouriers();
  const [picked, setPicked] = useState<string | null>(null);
  const courier = picked ?? couriers[0]?.courierCode ?? '';
  const [trackingNo, setTrackingNo] = useState('');
  const [pickupCodeInput, setPickupCodeInput] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setReason('');
      setPicked(null);
      setTrackingNo('');
      setPickupCodeInput('');
      setLocalError(null);
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (reason.trim().length === 0) {
      setLocalError(t('orders.actionModal.reasonRequired'));
      return;
    }
    if (requiresTracking(action)) {
      if (trackingNo.trim().length === 0) {
        setLocalError(t('orders.actionModal.trackingNoRequired'));
        return;
      }
    }
    if (requiresPickupCodeConfirm(action)) {
      if (pickupCodeInput.trim().length === 0) {
        setLocalError(t('orders.actionModal.pickupCodeRequired'));
        return;
      }
      if (
        typeof expectedPickupCode === 'string' &&
        expectedPickupCode.length > 0 &&
        pickupCodeInput.trim() !== expectedPickupCode
      ) {
        setLocalError(t('orders.actionModal.pickupCodeMismatch'));
        return;
      }
    }
    setLocalError(null);

    const body: AdminOrderActionRequest = { reason: reason.trim() };
    if (requiresTracking(action)) {
      // 생성 타입(CourierCode)은 옛 고정 6개라 마스터 코드(문자열)로 넓혀 보낸다 — BE 가 옛 값도 받는다(QA-510).
      body.courierCode = courier as NonNullable<AdminOrderActionRequest['courierCode']>;
      body.trackingNo = trackingNo.trim();
    }
    onSubmit(body);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-md">
        <DialogTitle>{t(`orders.actions.${action}`)}</DialogTitle>
        <DialogDescription>
          {t('orders.actionModal.subject', { orderNo })}
        </DialogDescription>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex flex-col">
            <label
              htmlFor="action-reason"
              className="text-sm text-[color:var(--content-muted)]"
            >
              {t('orders.actionModal.reason')}
            </label>
            <Input
              id="action-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              required
            />
          </div>

          {requiresTracking(action) ? (
            <>
              <div className="flex flex-col">
                <label
                  htmlFor="action-courier"
                  className="text-sm text-[color:var(--content-muted)]"
                >
                  {t('orders.actionModal.courier')}
                </label>
                <Select
                  id="action-courier"
                  value={courier}
                  onChange={(e) => setPicked(e.target.value)}
                >
                  {couriers.map((c) => (
                    <option key={c.courierCode} value={c.courierCode}>
                      {c.nameKo}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="flex flex-col">
                <label
                  htmlFor="action-tracking"
                  className="text-sm text-[color:var(--content-muted)]"
                >
                  {t('orders.actionModal.trackingNo')}
                </label>
                <Input
                  id="action-tracking"
                  value={trackingNo}
                  onChange={(e) => setTrackingNo(e.target.value)}
                  maxLength={100}
                />
              </div>
            </>
          ) : null}

          {requiresPickupCodeConfirm(action) ? (
            <div className="flex flex-col">
              <label
                htmlFor="action-pickup-code"
                className="text-sm text-[color:var(--content-muted)]"
              >
                {t('orders.actionModal.pickupCodeConfirm')}
              </label>
              <Input
                id="action-pickup-code"
                value={pickupCodeInput}
                onChange={(e) => setPickupCodeInput(e.target.value)}
                maxLength={16}
                autoComplete="off"
              />
            </div>
          ) : null}

          {localError ? (
            <p
              className="text-sm"
              role="alert"
              style={{ color: 'var(--status-danger-fg)' }}
            >
              {localError}
            </p>
          ) : null}

          {errorMessage ? (
            <p
              className="text-sm"
              role="alert"
              style={{ color: 'var(--status-danger-fg)' }}
            >
              {errorMessage}
            </p>
          ) : null}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              size="md"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              {t('orders.actions.cancel')}
            </Button>
            <Button type="submit" variant="cta" size="md" disabled={isSubmitting}>
              {isSubmitting
                ? t('orders.actionModal.submitting')
                : t('orders.actionModal.confirm')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
